import React, {useEffect} from 'react'
import {useSelector} from 'react-redux'
import {useDispatch} from 'react-redux'
import styled from '@emotion/styled'
import {
  getAccommodationPageHotelId,
  getFrozenBookingOffer
} from 'modules/accommodation/selectors'
import {
  getDealDetailsOverlay,
  getDealDetailsOverlayVisibility
} from 'modules/overlay/selectors'
import {hideOverlay} from 'modules/overlay/slice'
import {
  getNumberOfGuests,
  getNumberOfRooms,
  getSearchParameters
} from 'modules/sapiSearch/selectors'
import {
  getAccommodationPageHotelTimezone,
  getFilteredRoomsData,
  getIsRoomsPollingComplete,
  getRoomById,
  getRoomOfferById
} from 'modules/searchApi/selectors'

import {BottomSheet} from '@vio/atlas/BottomSheet'
import {useDeviceLayout} from '@vio/atlas/context/deviceLayout'
import {Box} from '@vio/atlas/helpers/Box'
import {Content, PopUp} from '@vio/atlas/PopUp'
import {cssTheme} from '@vio/atlas/src/themes'
import {linariaMq} from '@vio/atlas/src/utils/breakpoints'
import {Text} from '@vio/atlas/Text'
import FormattedMessageWrapper from '@vio/core/src/localization/components/FormattedMessage'
import {Offer} from '@vio/core/src/offer/types/offer'
import {isRoomlessRoom} from '@vio/core/src/room/business/utils/roomless'
import {Room, SplitBooking} from '@vio/core/src/room/types/room'
import {DealDetailsMeal} from '@vio/shared/src/booking/DealDetails/DealDetailsMeal'

import {useGuestSplit} from '../Rooms/hooks/useGuestSplit'
import {DealDetailsBottomCTA} from './DealDetailsBottomCTA'
import {DealDetailsCancellation} from './DealDetailsCancellation'
import {DealDetailsOverview} from './DealDetailsOverview'
import {DealDetailsPriceBreakdown} from './DealDetailsPriceBreakdown'
import {DealDetailsSplitBookingOverview} from './DealDetailsSplitBookingOverview'
import {PaymentMethods} from './PaymentMethods'

const DealDetailsPopUp = styled(PopUp)`
  ${Content} {
    max-height: calc(100dvh - 80px);
  }
`

const Title = () => (
  <Text variant="titleM">
    <FormattedMessageWrapper
      id="dealDetails.title"
      defaultMessage="Deal details"
    />
  </Text>
)

const DealDetailsContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${cssTheme.layout.spacing.s300};
  background-color: ${cssTheme.colors.background.neutral.secondary};
  ${linariaMq.desktopXs} {
    gap: ${cssTheme.layout.spacing.s000};
  }

  > div {
    padding: ${cssTheme.layout.spacing.s500};
    background-color: ${cssTheme.colors.background.neutral.primary};
    ${linariaMq.desktopXs} {
      &:not(:first-child) {
        border-top: 1px solid ${cssTheme.colors.border.neutral.secondary};
      }
    }
  }

  > div:last-child {
    padding-bottom: 0;

    & > div {
      padding-bottom: ${cssTheme.layout.spacing.s700};
    }
  }
`
export interface DealDetailsContentProps {
  room?: Room
  offer?: Offer
  splitBooking?: SplitBooking
  bookingId?: string
}

export const DealDetailsContent = ({
  room,
  offer,
  splitBooking,
  bookingId
}: DealDetailsContentProps) => {
  const isSplitBooking = Boolean(splitBooking)
  const hotelId = useSelector(getAccommodationPageHotelId)
  const numberOfGuests = useSelector(getNumberOfGuests)
  const numberOfRooms = useSelector(state => getNumberOfRooms(state, hotelId))
  const guestSplit = useGuestSplit(hotelId)
  const {checkIn, checkOut} = useSelector(getSearchParameters) || {}
  const hotelTimeZone = useSelector(getAccommodationPageHotelTimezone)

  const offerOrSplitBooking = splitBooking || offer

  if (!offerOrSplitBooking) {
    return null
  }
  const {cancellationPenalties, prices} = offerOrSplitBooking

  const splitBookingHasNoMeals = splitBooking?.offers?.every(
    offer => !offer.offer.mealPlan?.length
  )
  return (
    <DealDetailsContentWrapper>
      {isSplitBooking ? (
        <DealDetailsSplitBookingOverview splitBooking={splitBooking} />
      ) : (
        <DealDetailsOverview
          isRoomless={isRoomlessRoom(room)}
          roomName={room?.name ?? ''}
          roomImage={room?.images?.[0]?.url}
          checkIn={checkIn}
          checkOut={checkOut}
          numberOfGuests={numberOfGuests}
          numberOfRooms={numberOfRooms}
          guestSplit={guestSplit}
        />
      )}
      <div data-id="deal-details-cancellation-penalties">
        <DealDetailsCancellation
          cancellationPenalties={cancellationPenalties}
          prices={prices}
          hotelTimeZone={hotelTimeZone}
        />
      </div>

      {!isSplitBooking && <DealDetailsMeal mealPlan={offer?.mealPlan || []} />}
      {isSplitBooking && splitBookingHasNoMeals && (
        <DealDetailsMeal mealPlan={[]} />
      )}
      <div data-id="deal-details-payment-methods">
        <PaymentMethods
          offer={offer}
          bundleId={splitBooking?.bundleId}
          cancellationPenalties={cancellationPenalties}
          bookingId={bookingId}
        />
      </div>
      <div data-id="deal-details-price-breakdown">
        <DealDetailsPriceBreakdown
          prices={prices}
          paymentMode={offer?.paymentMode}
          checkIn={checkIn}
          checkOut={checkOut}
          numberOfGuests={numberOfGuests}
          numberOfRooms={numberOfRooms}
          isSplitBooking={isSplitBooking}
        />
      </div>
    </DealDetailsContentWrapper>
  )
}

export const DealDetailsOverlay = () => {
  const dispatch = useDispatch()
  const {isMobile} = useDeviceLayout()
  const dealDetailsOverlayVisibility = useSelector(
    getDealDetailsOverlayVisibility
  )
  const {offerId, roomId, bundleId, isFrozen} =
    useSelector(getDealDetailsOverlay) || {}
  const room = useSelector(state => getRoomById(state, roomId))
  const {offer: frozenOffer, bookingId} = useSelector(state =>
    getFrozenBookingOffer(state, [roomId, ...(room?.squashedIds || [])])
  )
  const isRoomsComplete = useSelector(getIsRoomsPollingComplete)

  const roomOffer = useSelector(state => getRoomOfferById(state, offerId))
  const offer = isFrozen ? frozenOffer : roomOffer
  const {splitBooking: splitBookingData} = useSelector(getFilteredRoomsData)

  const onClose = () => {
    dispatch(hideOverlay())
  }
  const splitBooking =
    bundleId && splitBookingData?.bundleId === bundleId
      ? splitBookingData
      : undefined
  const hasMatchingDeal = Boolean(offer || splitBooking)
  const isOpen = dealDetailsOverlayVisibility && hasMatchingDeal

  useEffect(() => {
    // Hide overlay if offer persisted in history is expired
    if (!hasMatchingDeal && isRoomsComplete && dealDetailsOverlayVisibility) {
      dispatch(hideOverlay())
    }
  }, [hasMatchingDeal, dispatch, dealDetailsOverlayVisibility, isRoomsComplete])

  if (isMobile) {
    return (
      <BottomSheet
        isOpen={isOpen}
        navBarProps={{centerContent: <Title />}}
        onClose={onClose}
        noPaddings
        snapPoints={{top: 'nearFull', middle: 0.95}}
        bottomBarContent={
          <DealDetailsBottomCTA
            room={room}
            offer={offer}
            splitBooking={splitBooking}
            isFrozen={isFrozen}
            bookingId={bookingId}
          />
        }
      >
        <DealDetailsContent
          room={room}
          offer={offer}
          splitBooking={splitBooking}
          bookingId={bookingId}
        />
      </BottomSheet>
    )
  }

  return (
    <DealDetailsPopUp
      width={1000}
      closeOnOutsideClick
      isOpen={isOpen}
      navBarProps={{centerContent: <Title />, withDivider: true}}
      closeAction={onClose}
      noPaddings
      footer={
        <DealDetailsBottomCTA
          room={room}
          offer={offer}
          splitBooking={splitBooking}
          isFrozen={isFrozen}
          bookingId={bookingId}
        />
      }
    >
      <Box width="480px">
        <DealDetailsContent
          room={room}
          offer={offer}
          splitBooking={splitBooking}
          bookingId={bookingId}
        />
      </Box>
    </DealDetailsPopUp>
  )
}
